import {Injectable} from '@angular/core';
import {firstValueFrom, Observable} from 'rxjs';
import {TeamsContextService} from '../teams-context.service';
import {switchMap, take} from 'rxjs/operators';
import {app} from '@microsoft/teams-js';
import {
    LearningProgressInsightService
} from '../../../../../micromate-learn-lib/src/lib/services/LearningProgressInsightService';
import {
    StudyProgressRessource
} from '../../../../../micromate-learn-lib/src/lib/services/rest/study-progress-ressource.service';
import {
    LearningPackageInsightsDetailsResponseDto
} from '../../../../../micromate-learn-lib/src/lib/services/rest/api-types/LearningPackageInsightsDetailsResponseDto';
import {
    ConversationRessource
} from '../../../../../micromate-learn-lib/src/lib/services/rest/conversation-ressource.service';
import {TeamsChannelDetectorService} from '../teams-channel-detector.service';
import {
    LearningPackageInsightsQuestionsResponseDto
} from '../../../../../micromate-learn-lib/src/lib/services/rest/api-types/LearningPackageInsightsQuestionsResponseDto';
import {TimePeriodMode} from '../../../../../micromate-learn-lib/src/lib/services/rest/api-types/TimePeriodMode';
import {
    LearningProgressHistoryResponseDto
} from '../../../../../micromate-learn-lib/src/lib/services/rest/api-types/LearningProgressHistoryResponseDto';
import {
    LearningPackageInsightsSkillsProfileResponseDto
} from '../../../../../micromate-learn-lib/src/lib/services/rest/api-types/LearningPackageInsightsSkillsProfileResponseDto';
import {
    LearningPackageInsightsTopicsResponseDto
} from '../../../../../micromate-learn-lib/src/lib/services/rest/api-types/LearningPackageInsightsTopicsResponseDto';

@Injectable({
    providedIn: 'root'
})
export class ProfileLearningProgressInsightService implements LearningProgressInsightService {

    constructor(private teamsContextService: TeamsContextService,
                private studyProgressRessource: StudyProgressRessource,
                private conversationService: ConversationRessource,
                private teamsChannelDetector: TeamsChannelDetectorService) {
    }

    public getLearningProgressInsightDetails(learningPackageId: string): Observable<LearningPackageInsightsDetailsResponseDto> {
        return this.teamsContextService.teamsContext$.pipe(
            take(1),
            switchMap((teamsContext: app.Context) => {
                const userId = teamsContext.user!.id;
                return this.studyProgressRessource.getLearnerLearningProgressInsightDetails(learningPackageId, userId);
            })
        );
    }

    public getLearningProgressInsightQuestionsOverview(learningPackageId: string): Observable<LearningPackageInsightsQuestionsResponseDto> {
        return this.teamsContextService.teamsContext$.pipe(
            take(1),
            switchMap((teamsContext: app.Context) => {
                const userId = teamsContext.user!.id;
                return this.studyProgressRessource.getLearnerLearningProgressQuestionsDetails(learningPackageId, userId);
            })
        );
    }

    public getLearningProgressInsightTopicsOverview(learningPackageId: string): Observable<LearningPackageInsightsTopicsResponseDto> {
        return this.teamsContextService.teamsContext$.pipe(
            take(1),
            switchMap((teamsContext: app.Context) => {
                const userId = teamsContext.user!.id;
                return this.studyProgressRessource.getLearnerLearningProgressInsightTopics(learningPackageId, userId);
            })
        );
    }

    public async learnLearningPackage(learningPackageId: string): Promise<void> {
        const teamsContext = await firstValueFrom(this.teamsContextService.teamsContext$.pipe(take(1)));
        const userId = teamsContext.user!.id;
        void this.conversationService.selectLearningPackageInProfile(learningPackageId, userId);

        void app.openLink(this.teamsChannelDetector.getMicrosoftTeamsChatChannelUrl());
    }

    public getLearningProgressInsightHistory(learningPackageId: string, timePeriodMode: TimePeriodMode): Observable<LearningProgressHistoryResponseDto> {
        return this.teamsContextService.teamsContext$.pipe(
            take(1),
            switchMap((teamsContext: app.Context) => {
                const userId = teamsContext.user!.id;
                return this.studyProgressRessource.getLearnerLearningProgressHistory(learningPackageId, userId, timePeriodMode)
            })
        );
    }

    public getSkillsProfileInsightHistory(learningPackageId: string): Observable<LearningPackageInsightsSkillsProfileResponseDto> {
        return this.teamsContextService.teamsContext$.pipe(
            take(1),
            switchMap((teamsContext: app.Context) => {
                const userId = teamsContext.user!.id;
                return this.studyProgressRessource.getLearnerLearningProgressInsightSkillsProfile(learningPackageId, userId);
            })
        );
    }
}
